/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import {
  boatus,
  imgElementSvg,
  mapMarkerPinWrapper,
} from '../../../components/shared/vector-images';

import Text from '../../../components/shared/_deprecated/elements/Text/Text';
import { callEndpoint } from '../../backend';
import { captureEvent } from '../../event-capture';
import { definitions, subscribe } from '../../message-broker';
import { ROUTE_MARKER_SUMMARY } from '../../route/route-names';
import { transitionToNextRoute } from '../../route/route-navigation';
import { getStore } from '../../store';
import {
  layerExists,
  setDataSource,
  sourceExists,
  toggleLayerIfRequired,
} from '../_helpers';

let mapReference;
let currentViewPort;
let lastViewPortBounds;
const layerName = 'boatUsMarker';

const addLayersIfRequired = () => {
  if (!mapReference) return;

  if (!sourceExists(mapReference, layerName)) {
    mapReference.addSource(layerName, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }

  if (!layerExists(mapReference, layerName)) {
    mapReference.addLayer({
      id: layerName,
      type: 'symbol',
      source: layerName,
      layout: {
        'icon-image': 'boatUsMarker',
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          10.9999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.25],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.6, 0.4],
        ],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
};

const layer = {
  displayName: 'Boat US',
  synonyms: 'boatus',
  group: 'standardMarker',
  typeUrl: 'boatus',
  icon: {
    baseShape: boatus,
    iconColor: '#EEEEEE',
    markerColor: '#EEEEEE',
  },
  shownByDefault: true,
  relativeDepth: 0,
  menuOptions: {
    visible: false,
    owningMenu: 'general-services',
    order: 20,
  },
  standardMarkerOptions: {
    markerType: layerName,
    allowCreation: false,
    allowEditing: false,
    allowComments: false,
    viewOptions: {
      typeTitle: 'Event',
      itemTitleFunction: (item) => (item && item.title) || 'Event',
    },
    creationOptions: {
      menuText: 'Event',
      newTitle: 'Event',
      titlePlaceholder: 'Name of event',
      markerTypeDescription: [
        <Text key="description-text-1" size="-1" paragraph>
          This marker is used to indicate an event
        </Text>,
      ],
      checkBoxItems: [],
    },
    editingOptions: {},
  },
  popOverConfiguration: (marker) => ({
    markerType: layer.standardMarkerOptions.markerType,
    id: marker.id,
    displayChat: false,
    displayUpVotes: false,
    title: 'Event',
    description: marker.title,
  }),
};

const loadMarkerImages = async (map) => {
  const marker = await imgElementSvg(
    mapMarkerPinWrapper(layer.icon.baseShape()),
    {
      fillColor: layer.icon.iconColor,
      markerColor: layer.icon.markerColor,
      width: 90,
      height: 90,
    },
  );
  marker.width = '90';
  marker.height = '90';

  if (!map.hasImage(layerName)) map.addImage(layerName, marker);
};

const updateData = async (force) => {
  // If we have yet to receive a reference to the map object through the onInitialize function,
  // just return as we can't update the data on the layer if the layer doesn't exist yet.
  if (!mapReference) return;

  addLayersIfRequired();

  const state = getStore().getState();

  const { hidingAllLayers } = state.appControls;

  if (hidingAllLayers) {
    toggleLayerIfRequired(mapReference, layerName, false);
    return;
  }

  toggleLayerIfRequired(mapReference, layerName, true);

  let bounds = state.appControls?.currentMapParams?.viewPortDetails?.bounds;

  // If there isn't a view port to query for bounds, don't do anything.
  if (!bounds) {
    if (!currentViewPort) {
      return;
    }

    bounds = currentViewPort.bounds;
  }

  // If we're not forcing the update, and the bounds are the same as the
  // previous call, don't do anything
  if (!force && bounds === lastViewPortBounds) return;
  lastViewPortBounds = bounds;

  const results = await callEndpoint(
    callEndpoint.endpoints.mapSearchQueryByBoundingBoxAndType,
    {
      mapMarkerTypes: ['boatUsMarker'],
      boundingBox: bounds,
    },
  );

  const geojson = {
    type: 'FeatureCollection',
    features: results?.map((item) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.latLon.lon, item.latLon.lat],
      },
      properties: {
        ...item,
        image: 'boatUsMarker',
        markerSelected: window?.location?.href?.includes(item?.id),
      },
    })),
  };

  setDataSource(mapReference, layerName, geojson);
};

layer.onInitialize = async (map) => {
  mapReference = map;
  addLayersIfRequired();

  await loadMarkerImages(mapReference, layer);

  map.on('deckee-press', layerName, async (event) => {
    // This is to select a marker and go to the popover
    const { id } = event.features[0].properties;
    if (!event.originalEvent.defaultPrevented) {
      // Prevent the map click event from propagating to other layers
      event.originalEvent.preventDefault();

      captureEvent({
        type: 'marker: click',
        payload: {
          markerType: 'boatUsMarker',
          id,
          clickedLatitude: event.lngLat.lat,
          clickedLongitude: event.lngLat.lng,
        },
      });
      transitionToNextRoute(ROUTE_MARKER_SUMMARY, {
        markerType: 'boatUsMarker',
        id,
      });
    }
  });
};

layer.onToggle = (map, show) => {
  toggleLayerIfRequired(map, layerName, show);
};

subscribe(definitions.map.viewPortUpdated, (viewPort) => {
  currentViewPort = viewPort;

  updateData();
});

export default layer;
